import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { PlayCircleIcon } from 'lucide-react';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
export function ClosingSlide() {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-203">
      {/* ===== BACKGROUND ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-204">
        {/* Soft radial glow top-right */}
        <div className="absolute -top-60 -right-60 w-[70vmax] h-[70vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.07) 0%, transparent 65%)'
      }} data-id="element-205" />
        {/* Soft radial glow bottom-left */}
        <div className="absolute -bottom-60 -left-60 w-[60vmax] h-[60vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.05) 0%, transparent 65%)'
      }} data-id="element-206" />
        {/* Faint network pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg" data-id="element-207">
          <defs data-id="element-208">
            <pattern id="closing-net" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse" data-id="element-209">
              <circle cx="40" cy="40" r="1.5" fill="#0B1328" data-id="element-210" />
              <line x1="40" y1="40" x2="80" y2="0" stroke="#0B1328" strokeWidth="0.3" data-id="element-211" />
              <line x1="40" y1="40" x2="0" y2="80" stroke="#0B1328" strokeWidth="0.3" data-id="element-212" />
              <line x1="40" y1="40" x2="80" y2="80" stroke="#0B1328" strokeWidth="0.3" data-id="element-213" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#closing-net)" data-id="element-214" />
        </svg>
        {/* Subtle center spotlight */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50vmax] h-[50vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.04) 0%, transparent 60%)'
      }} data-id="element-215" />
      </div>

      {/* ===== TOP BAR ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.9,
      delay: 0.15,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1.5rem,3.5vh,3rem)] px-[clamp(1.75rem,4.5vw,4.5rem)] shrink-0" data-id="element-216">
        <SharjahPoliceEmblem variant="light" data-id="element-217" />
        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.5rem,5vh,3.5rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-218" />
      </motion.div>

      {/* ===== CENTER CONTENT ===== */}
      <div className="relative z-20 flex-1 flex flex-col items-center justify-center gap-10" data-id="element-219">
        {/* Thank you title */}
        <motion.h1 initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={base ?? {
        duration: 1,
        delay: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="font-arabic-display font-black text-center" style={{
        color: '#0B1328',
        fontSize: 'clamp(3.5rem, 7vw, 6rem)',
        letterSpacing: '-0.02em'
      }} data-id="element-220">
          شكراً لكم
        </motion.h1>

        {/* Teal accent line */}
        <motion.div initial={{
        opacity: 0,
        scaleX: 0
      }} animate={{
        opacity: 1,
        scaleX: 1
      }} transition={base ?? {
        duration: 0.7,
        delay: 0.6
      }} className="h-[3px] rounded-full" style={{
        width: 'clamp(4rem, 8vw, 6rem)',
        background: '#2FA99C'
      }} data-id="element-221" />

        {/* CTA Button */}
        <motion.a href="https://fms.pair2.ae" target="_blank" rel="noopener noreferrer" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={base ?? {
        duration: 0.8,
        delay: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} whileHover={{
        scale: 1.04,
        y: -2
      }} whileTap={{
        scale: 0.98
      }} className="flex items-center gap-3 rounded-2xl cursor-pointer transition-shadow duration-300 no-underline" style={{
        background: '#2FA99C',
        color: '#FFFFFF',
        padding: 'clamp(1rem, 2vh, 1.5rem) clamp(2.5rem, 5vw, 4rem)',
        fontSize: 'clamp(1.1rem, 1.6vw, 1.4rem)',
        fontWeight: 700,
        boxShadow: '0 12px 36px -8px rgba(47,169,156,0.4), 0 4px 12px rgba(47,169,156,0.15)',
        border: 'none',
        outline: 'none',
        textDecoration: 'none'
      }} onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 16px 48px -8px rgba(47,169,156,0.5), 0 6px 16px rgba(47,169,156,0.2)';
      }} onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 12px 36px -8px rgba(47,169,156,0.4), 0 4px 12px rgba(47,169,156,0.15)';
      }} data-id="element-222">
          <span className="font-arabic-display" data-id="element-223">عرض تجربة مباشرة للنظام</span>
          <PlayCircleIcon className="w-6 h-6" data-id="element-224" />
        </motion.a>
      </div>

      {/* ===== BOTTOM PAIR2 WATERMARK ===== */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={base ?? {
      duration: 1,
      delay: 1.2
    }} className="relative z-20 pb-[clamp(1.5rem,3vh,2.5rem)] text-center" data-id="element-225">
        <span className="font-arabic-body" style={{
        color: 'rgba(11,19,40,0.3)',
        fontSize: 'clamp(0.75rem, 0.9vw, 0.9rem)'
      }} data-id="element-226">
          Pair² — Smart Fleet & Driver Management
        </span>
      </motion.div>
    </section>;
}